import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card, Table, Button, Space, Tag, Modal, Form, Input, Switch,
  Typography, Row, Col, Statistic, Popconfirm, Tooltip, message,
  Alert, Tabs, InputNumber,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined,
  CarOutlined, EnvironmentOutlined, NodeIndexOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import TrainsTab from './TrainsTab';
import StopsMapTab from './StopsMapTab';
import RouteCompositionDrawer from './RouteCompositionDrawer';
import {
  transportApi, TransportRoute, TransportStop,
  RouteCreatePayload, StopCreatePayload,
} from '../../api/transport';

const { Title, Text } = Typography;

const TransportPage: React.FC = () => {
  const [routes, setRoutes] = useState<TransportRoute[]>([]);
  const [stops, setStops] = useState<TransportStop[]>([]);
  const [loadingRoutes, setLoadingRoutes] = useState(false);
  const [loadingStops, setLoadingStops] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('routes');

  const [routeModalOpen, setRouteModalOpen] = useState(false);
  const [editingRoute, setEditingRoute] = useState<TransportRoute | null>(null);
  const [routeForm] = Form.useForm();

  const [compositionRoute, setCompositionRoute] = useState<TransportRoute | null>(null);
  const [stopModalOpen, setStopModalOpen] = useState(false);
  const [editingStop, setEditingStop] = useState<TransportStop | null>(null);
  const [stopForm] = Form.useForm();

  const [submitting, setSubmitting] = useState(false);
  const [stopsSearch, setStopsSearch] = useState('');
  const [messageApi, contextHolder] = message.useMessage();

  const filteredStops = useMemo(() => {
    const q = stopsSearch.trim().toLowerCase();
    if (!q) return stops;
    return stops.filter(s => {
      const ru = (s.name_ru || '').toLowerCase();
      const kk = (s.name_kk || '').toLowerCase();
      return ru.includes(q) || kk.includes(q) || String(s.id).includes(q);
    });
  }, [stops, stopsSearch]);

  const fetchRoutes = useCallback(async () => {
    setLoadingRoutes(true);
    try {
      const res = await transportApi.listRoutes();
      setRoutes(res.data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Ошибка загрузки маршрутов');
    } finally {
      setLoadingRoutes(false);
    }
  }, []);

  const fetchStops = useCallback(async () => {
    setLoadingStops(true);
    try {
      const res = await transportApi.listStops();
      setStops(res.data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Ошибка загрузки остановок');
    } finally {
      setLoadingStops(false);
    }
  }, []);

  useEffect(() => { fetchRoutes(); fetchStops(); }, [fetchRoutes, fetchStops]);

  // Routes CRUD
  const openCreateRoute = () => {
    setEditingRoute(null);
    routeForm.resetFields();
    routeForm.setFieldsValue({ is_active: true });
    setRouteModalOpen(true);
  };

  const openEditRoute = (r: TransportRoute) => {
    setEditingRoute(r);
    // schedule НЕ кладём в форму: это словарь с полилиниями, а не текст
    routeForm.setFieldsValue({
      number: r.number,
      name_ru: r.name_ru,
      name_kk: r.name_kk,
      is_active: r.is_active,
    });
    setRouteModalOpen(true);
  };

  const handleRouteSubmit = async () => {
    let values: any;
    try { values = await routeForm.validateFields(); } catch { return; }
    setSubmitting(true);
    try {
      if (editingRoute) {
        await transportApi.updateRoute(editingRoute.id, values);
        messageApi.success('Маршрут обновлён');
      } else {
        await transportApi.createRoute(values as RouteCreatePayload);
        messageApi.success('Маршрут создан');
      }
      setRouteModalOpen(false);
      fetchRoutes();
    } catch (err: any) {
      messageApi.error(err?.response?.data?.detail || 'Ошибка');
    } finally {
      setSubmitting(false);
    }
  };

  const deleteRoute = async (id: number) => {
    try {
      await transportApi.deleteRoute(id);
      messageApi.success('Маршрут удалён');
      fetchRoutes();
    } catch (err: any) {
      messageApi.error(err?.response?.data?.detail || 'Ошибка');
    }
  };

  // Stops CRUD
  const openCreateStop = () => {
    setEditingStop(null);
    stopForm.resetFields();
    setStopModalOpen(true);
  };

  const openEditStop = (s: TransportStop) => {
    setEditingStop(s);
    stopForm.setFieldsValue(s);
    setStopModalOpen(true);
  };

  const handleStopSubmit = async () => {
    let values: any;
    try { values = await stopForm.validateFields(); } catch { return; }
    setSubmitting(true);
    try {
      if (editingStop) {
        await transportApi.updateStop(editingStop.id, values);
        messageApi.success('Остановка обновлена');
      } else {
        await transportApi.createStop(values as StopCreatePayload);
        messageApi.success('Остановка создана');
      }
      setStopModalOpen(false);
      fetchStops();
    } catch (err: any) {
      messageApi.error(err?.response?.data?.detail || 'Ошибка');
    } finally {
      setSubmitting(false);
    }
  };

  const deleteStop = async (id: number) => {
    try {
      await transportApi.deleteStop(id);
      messageApi.success('Остановка удалена');
      fetchStops();
    } catch (err: any) {
      messageApi.error(err?.response?.data?.detail || 'Ошибка');
    }
  };

  const activeRoutes = routes.filter(r => r.is_active).length;

  const routeColumns: ColumnsType<TransportRoute> = [
    {
      title: 'Номер',
      dataIndex: 'number',
      key: 'number',
      width: 100,
      render: (n: string) => (
        <span className="num" style={{
          fontSize: 12, fontWeight: 500, border: '1px solid var(--l-line)', borderRadius: 5,
          padding: '1px 7px', color: 'var(--l-ink)', background: 'var(--l-surface)',
        }}>{n}</span>
      ),
    },
    {
      title: 'Название',
      key: 'name',
      ellipsis: true,
      render: (_: any, r: TransportRoute) => (
        <Space direction="vertical" size={0}>
          <Text strong>{r.name_ru || '—'}</Text>
          {r.name_kk && <Text type="secondary" style={{ fontSize: 12 }}>{r.name_kk}</Text>}
        </Space>
      ),
    },
    {
      title: 'Остановок',
      dataIndex: 'stops_count',
      key: 'stops_count',
      width: 110,
      align: 'center',
      render: (c: number) => <Tag icon={<NodeIndexOutlined />}>{c}</Tag>,
    },
    {
      title: 'Линия на карте',
      key: 'geometry',
      width: 130,
      render: (_: any, r: TransportRoute) => {
        const meta = (r.geometry_meta || r.schedule) as any;
        const has = Array.isArray(meta?.directions) && meta.directions.length > 0;
        return has
          ? <Tag>есть</Tag>
          : <Tag>нет</Tag>;
      },
    },
    {
      title: 'Статус',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      render: (active: boolean) => active ? <Tag color="success">Активен</Tag> : <Tag color="default">Отключён</Tag>,
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 100,
      render: (_: any, record: TransportRoute) => (
        <Space size={4}>
          <Button size="small" icon={<NodeIndexOutlined />} onClick={() => setCompositionRoute(record)}>
            Состав
          </Button>
          <Tooltip title="Редактировать"><Button size="small" icon={<EditOutlined />} onClick={() => openEditRoute(record)} /></Tooltip>
          <Popconfirm title="Удалить маршрут?" onConfirm={() => deleteRoute(record.id)} okText="Да" cancelText="Нет">
            <Tooltip title="Удалить"><Button size="small" icon={<DeleteOutlined />} danger /></Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const stopColumns: ColumnsType<TransportStop> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: 'Название',
      key: 'name',
      render: (_: any, r: TransportStop) => (
        <Space direction="vertical" size={0}>
          <Text strong>{r.name_ru || '—'}</Text>
          {r.name_kk && <Text type="secondary" style={{ fontSize: 12 }}>{r.name_kk}</Text>}
        </Space>
      ),
    },
    {
      title: 'Координаты',
      key: 'coords',
      width: 200,
      render: (_: any, r: TransportStop) =>
        r.latitude && r.longitude
          ? <Text code style={{ fontSize: 12 }}>{r.latitude?.toFixed(5)}, {r.longitude?.toFixed(5)}</Text>
          : <Text type="secondary">—</Text>,
    },
    {
      title: 'Маршрутов',
      key: 'route_count',
      dataIndex: 'route_count',
      width: 120,
      align: 'center',
      render: (c: number | undefined) => {
        const count = typeof c === 'number' ? c : 0;
        if (count > 0) return <span className="num" style={{ fontSize: 12 }}>{count}</span>;
        return <span style={{ fontSize: 11.5, color: 'var(--l-faint)' }}>не используется</span>;
      },
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 100,
      render: (_: any, record: TransportStop) => (
        <Space size={4}>
          <Tooltip title="Редактировать"><Button size="small" icon={<EditOutlined />} onClick={() => openEditStop(record)} /></Tooltip>
          <Popconfirm title="Удалить остановку?" onConfirm={() => deleteStop(record.id)} okText="Да" cancelText="Нет">
            <Tooltip title="Удалить"><Button size="small" icon={<DeleteOutlined />} danger /></Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {contextHolder}

      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <Title level={3} style={{ margin: 0, color: 'var(--l-ink)' }}>Транспорт</Title>
          <Text type="secondary">Маршруты и остановки общественного транспорта г. Аксай</Text>
        </div>
        <Space>
          {activeTab === 'routes' && (
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreateRoute} style={{ background: 'var(--l-accent)' }}>Добавить маршрут</Button>
          )}
          {activeTab === 'stops' && (
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreateStop} style={{ background: 'var(--l-accent)' }}>Добавить остановку</Button>
          )}
        </Space>
      </div>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card><Statistic title="Маршрутов" value={routes.length} prefix={<CarOutlined style={{ color: 'var(--l-muted)' }} />} /></Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card style={{ }}><Statistic title="Активных маршрутов" value={activeRoutes} valueStyle={{ color: 'var(--l-ok)' }} /></Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card style={{ }}><Statistic title="Остановок" value={stops.length} prefix={<EnvironmentOutlined style={{ color: 'var(--l-accent)' }} />} /></Card>
        </Col>
      </Row>

      {error && <Alert type="error" message={error} closable onClose={() => setError(null)} style={{ marginBottom: 16 }} />}

      <Card bodyStyle={{ padding: 0 }} extra={<Button icon={<ReloadOutlined />} onClick={() => { fetchRoutes(); fetchStops(); }}>Обновить</Button>}>
        <Tabs activeKey={activeTab} onChange={setActiveTab} style={{ padding: '0 16px' }} items={[
          { key: 'routes', label: `Маршруты (${routes.length})`, children: (
            <Table<TransportRoute> rowKey="id" columns={routeColumns} dataSource={routes} loading={loadingRoutes} pagination={{ pageSize: 20, showTotal: t => `Всего: ${t}` }} locale={{ emptyText: 'Маршруты не найдены' }} scroll={{ x: 700 }} />
          )},
          { key: 'stops', label: `Остановки (${stops.length})`, children: (
            <div>
              <div style={{ padding: '12px 16px' }}>
                <Input.Search
                  allowClear
                  placeholder="Поиск по названию или ID"
                  value={stopsSearch}
                  onChange={(e) => setStopsSearch(e.target.value)}
                  style={{ maxWidth: 400 }}
                />
                {stopsSearch && (
                  <Text type="secondary" style={{ marginLeft: 12 }}>
                    Найдено: {filteredStops.length} из {stops.length}
                  </Text>
                )}
              </div>
              <Table<TransportStop> rowKey="id" columns={stopColumns} dataSource={filteredStops} loading={loadingStops} pagination={{ pageSize: 20, showTotal: t => `Всего: ${t}` }} locale={{ emptyText: 'Остановки не найдены' }} scroll={{ x: 500 }} />
            </div>
          )},
          { key: 'map', label: `Карта остановок (${stops.filter(s => s.latitude != null && s.longitude != null).length})`, children: (
            <StopsMapTab stops={stops} onRefresh={fetchStops} />
          )},
          { key: 'trains', label: 'Поезда (ЖД)', children: <TrainsTab /> },
        ]} />
      </Card>

      {/* Состав маршрута: порядок остановок + линия на карте */}
      <RouteCompositionDrawer
        route={compositionRoute}
        allStops={stops}
        onClose={() => setCompositionRoute(null)}
        onSaved={() => { fetchRoutes(); }}
      />

      {/* Route Modal */}
      <Modal title={editingRoute ? 'Редактировать маршрут' : 'Добавить маршрут'} open={routeModalOpen} onCancel={() => setRouteModalOpen(false)} onOk={handleRouteSubmit} confirmLoading={submitting} okText={editingRoute ? 'Сохранить' : 'Создать'} cancelText="Отмена" width={560} destroyOnClose>
        <Form form={routeForm} layout="vertical" style={{ marginTop: 16 }}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="number" label="Номер" rules={[{ required: true, message: 'Введите номер' }]}>
                <Input placeholder="1, 2А, 15" />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item name="name_ru" label="Название (рус.)">
                <Input placeholder="Центр — Мкр.5" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="name_kk" label="Атауы (каз.)">
            <Input placeholder="Орталық — 5 шағын аудан" />
          </Form.Item>
          <Form.Item name="is_active" label="Активен" valuePropName="checked">
            <Switch checkedChildren="Да" unCheckedChildren="Нет" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Stop Modal */}
      <Modal title={editingStop ? 'Редактировать остановку' : 'Добавить остановку'} open={stopModalOpen} onCancel={() => setStopModalOpen(false)} onOk={handleStopSubmit} confirmLoading={submitting} okText={editingStop ? 'Сохранить' : 'Создать'} cancelText="Отмена" width={560} destroyOnClose>
        <Form form={stopForm} layout="vertical" style={{ marginTop: 16 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="name_ru" label="Название (рус.)">
                <Input placeholder="Центральный рынок" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="name_kk" label="Атауы (каз.)">
                <Input placeholder="Орталық базар" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="latitude" label="Широта" rules={[{ required: true, message: 'Укажите широту' }]}>
                <InputNumber style={{ width: '100%' }} placeholder="51.1694" step={0.0001} precision={6} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="longitude" label="Долгота" rules={[{ required: true, message: 'Укажите долготу' }]}>
                <InputNumber style={{ width: '100%' }} placeholder="51.3882" step={0.0001} precision={6} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default TransportPage;
